import { HiOutlineDocument, HiOutlineArrowDownTray, HiOutlineEye, HiOutlineXMark } from "react-icons/hi2";
import { cn } from "../../../lib/cn";

const STATUS_LABELS = {
  ATTACHED: "Attached",
  SUPERSEDED: "Superseded",
  UNAVAILABLE: "Unavailable",
};

const STATUS_DOT_COLORS = {
  ATTACHED: "bg-secondary",
  SUPERSEDED: "bg-warning",
  UNAVAILABLE: "bg-mediumgrey",
};

const formatSize = (bytes) => {
  if (bytes == null) return "";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  if (bytes < 1024 * 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  return `${(bytes / (1024 * 1024 * 1024)).toFixed(2)} GB`;
};

const FileAttachmentStatusBadge = ({ status }) => (
  <span className="inline-flex items-center gap-1.5 text-[11px] font-medium">
    <span className={cn("w-1.5 h-1.5 rounded-full", STATUS_DOT_COLORS[status] || "bg-mediumgrey")} aria-hidden="true" />
    <span className="text-grey uppercase tracking-wider">{STATUS_LABELS[status] || status}</span>
  </span>
);

const FileAttachmentRow = ({
  attachment,
  permissions,
  onPreview,
  onDownload,
  onDetach,
}) => {
  const isUnavailable = attachment.status === "UNAVAILABLE";

  return (
    <div
      className={cn(
        "flex items-center gap-3 rounded-xl border px-4 py-3 transition-colors",
        isUnavailable ? "border-surface-border bg-surface" : "border-surface-border bg-white",
      )}
    >
      <HiOutlineDocument className="h-5 w-5 shrink-0 text-grey" />
      <div className="flex flex-1 flex-col gap-0.5 min-w-0">
        <div className="flex items-center gap-2">
          <span className={cn("truncate text-sm font-medium", isUnavailable ? "text-grey line-through" : "text-darkgrey")}>
            {attachment.file?.original_name || "Unknown file"}
          </span>
          <FileAttachmentStatusBadge status={attachment.status} />
        </div>
        <div className="flex items-center gap-3 text-xs text-grey">
          <span>{formatSize(attachment.file?.size_bytes)}</span>
          {attachment.created_by_name && (
            <span>by {attachment.created_by_name}</span>
          )}
        </div>
        {isUnavailable && (
          <p className="text-xs text-grey mt-1">This file is no longer available.</p>
        )}
      </div>

      <div className="flex items-center gap-1 shrink-0">
        {!isUnavailable && (
          <>
            {onPreview && (
              <button type="button" onClick={() => onPreview(attachment)} className="rounded-lg p-2 text-grey hover:bg-surface-hover hover:text-darkgrey" aria-label="Preview">
                <HiOutlineEye className="h-4 w-4" />
              </button>
            )}
            {onDownload && (
              <button type="button" onClick={() => onDownload(attachment)} className="rounded-lg p-2 text-grey hover:bg-surface-hover hover:text-darkgrey" aria-label="Download">
                <HiOutlineArrowDownTray className="h-4 w-4" />
              </button>
            )}
          </>
        )}
        {permissions?.can_detach && onDetach && (
          <button type="button" onClick={() => onDetach(attachment)} className="rounded-lg p-2 text-grey hover:bg-surface-hover hover:text-red" aria-label="Detach">
            <HiOutlineXMark className="h-4 w-4" />
          </button>
        )}
      </div>
    </div>
  );
};

const FileAttachmentList = ({
  attachments = [],
  permissions,
  onPreview,
  onDownload,
  onDetach,
  emptyMessage = "No files attached.",
}) => {
  if (!attachments.length) {
    return (
      <div className="flex flex-col items-center gap-2 py-8 text-center">
        <HiOutlineDocument className="h-8 w-8 text-grey/40" />
        <p className="text-sm text-grey">{emptyMessage}</p>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-2">
      {attachments.map((attachment) => (
        <FileAttachmentRow
          key={attachment.id}
          attachment={attachment}
          permissions={permissions}
          onPreview={onPreview}
          onDownload={onDownload}
          onDetach={onDetach}
        />
      ))}
    </div>
  );
};

export { FileAttachmentRow, FileAttachmentStatusBadge };
export default FileAttachmentList;
